import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import {
  ProfileSetupProvider,
  useProfileSetup,
} from '../context/ProfileSetupContext';
import '../styles/ProfileSetup.css';
import { useAuth } from '../context/AuthContext'; // ← add this

const DENOMINATIONS = [
  'Church Of Christ',
  'Catholic',
  'Baptist',
  'Methodist',
  'Pentecostal',
  'Presbyterian',
  'Orthodox',
  'Anglican',
  'Evangelical',
  'Seventh-day Adventist',
  'Non-denominational',
  'Other',
];

const FAITH_LEVELS = [
  { value: 1, label: 'Exploring', description: 'Curious about faith' },
  { value: 2, label: 'Believer', description: 'Faith matters to me' },
  { value: 3, label: 'Practicing', description: 'Active in my walk' },
  { value: 4, label: 'Devoted', description: 'Faith shapes my life' },
  { value: 5, label: 'Faith-led', description: 'God leads everything' },
];

const INTERESTS = [
  'Worship', 'Bible study', 'Prayer', 'Hiking', 'Reading',
  'Cooking', 'Travel', 'Music', 'Movies', 'Sports',
  'Coffee', 'Volunteering', 'Photography', 'Dancing', 'Gaming',
  'Fitness', 'Art', 'Writing', 'Nature', 'Pets',
];

// =====================================================================
// Wrapper: provides the context, then renders the flow inside it
// =====================================================================
export default function ProfileSetupPage() {
  return (
    <ProfileSetupProvider>
      <ProfileSetupFlow />
    </ProfileSetupProvider>
  );
}

function ProfileSetupFlow() {
  const [step, setStep] = useState(1);

  return (
    <div className="setup-container">
      <div className="setup-card">
        <div className="setup-header">
          <h1>Set up your profile</h1>
          <p>Step {step} of 4</p>
        </div>

        <ProgressBar currentStep={step} />

        {step === 1 && <Step1Basics onNext={() => setStep(2)} />}
        {step === 2 && (
          <Step2Faith onNext={() => setStep(3)} onBack={() => setStep(1)} />
        )}
        {step === 3 && (
          <Step3About onNext={() => setStep(4)} onBack={() => setStep(2)} />
        )}
        {step === 4 && <Step4Photos onBack={() => setStep(3)} />}
      </div>
    </div>
  );
}

// =====================================================================
// Progress bar
// =====================================================================
function ProgressBar({ currentStep }) {
  const steps = ['Basics', 'Faith', 'About', 'Photos'];
  return (
    <div className="progress-bar">
      {steps.map((label, i) => {
        const num = i + 1;
        const status =
          num < currentStep ? 'done' : num === currentStep ? 'active' : 'todo';
        return (
          <div key={num} className={`progress-step ${status}`}>
            <div className="progress-dot">{num}</div>
            <span className="progress-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// =====================================================================
// STEP 1: Basics
// =====================================================================
function Step1Basics({ onNext }) {
  const { data, updateData } = useProfileSetup();
  const [errors, setErrors] = useState({});

  // Max date = today minus 18 years
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  )
    .toISOString()
    .split('T')[0];

  const validate = () => {
    const e = {};
    if (!data.full_name.trim() || data.full_name.trim().length < 2) {
      e.full_name = 'Please enter your full name';
    }
    if (!data.dob) {
      e.dob = 'Date of birth is required';
    } else {
      const age = calculateAge(data.dob);
      if (age < 18) e.dob = 'You must be at least 18 years old';
    }
    if (!data.gender) e.gender = 'Please select your gender';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="setup-form">
      <div className="form-group">
        <label>Full name</label>
        <input
          type="text"
          value={data.full_name}
          onChange={(e) => updateData({ full_name: e.target.value })}
          placeholder="Jane Doe"
          maxLength={100}
        />
        {errors.full_name && (
          <span className="error-text">{errors.full_name}</span>
        )}
      </div>

      <div className="form-group">
        <label>Date of birth</label>
        <input
          type="date"
          value={data.dob}
          max={maxDate}
          onChange={(e) => updateData({ dob: e.target.value })}
        />
        {errors.dob && <span className="error-text">{errors.dob}</span>}
      </div>

      <div className="form-group">
        <label>Gender</label>
        <div className="radio-row">
          <label className={`radio-card ${data.gender === 'male' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="gender"
              value="male"
              checked={data.gender === 'male'}
              onChange={() => updateData({ gender: 'male' })}
            />
            <span>Male</span>
          </label>
          <label className={`radio-card ${data.gender === 'female' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="gender"
              value="female"
              checked={data.gender === 'female'}
              onChange={() => updateData({ gender: 'female' })}
            />
            <span>Female</span>
          </label>
        </div>
        {errors.gender && <span className="error-text">{errors.gender}</span>}
      </div>

      <div className="setup-actions">
        <button className="primary-button" onClick={handleNext}>
          Continue
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// STEP 2: Faith
// =====================================================================
function Step2Faith({ onNext, onBack }) {
  const { data, updateData } = useProfileSetup();
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!data.denomination) e.denomination = 'Please select a denomination';
    if (!data.faith_level) e.faith_level = 'Please pick how faith fits in your life';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="setup-form">
      <div className="form-group">
        <label>Denomination</label>
        <select
          value={data.denomination}
          onChange={(e) => updateData({ denomination: e.target.value })}
        >
          <option value="">Select one...</option>
          {DENOMINATIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        {errors.denomination && (
          <span className="error-text">{errors.denomination}</span>
        )}
      </div>

      <div className="form-group">
        <label>Where are you in your faith?</label>
        <div className="faith-grid">
          {FAITH_LEVELS.map((lvl) => (
            <label
              key={lvl.value}
              className={`faith-card ${
                data.faith_level === lvl.value ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                name="faith_level"
                value={lvl.value}
                checked={data.faith_level === lvl.value}
                onChange={() => updateData({ faith_level: lvl.value })}
              />
              <div className="faith-card-num">{lvl.value}</div>
              <div className="faith-card-label">{lvl.label}</div>
              <div className="faith-card-desc">{lvl.description}</div>
            </label>
          ))}
        </div>
        {errors.faith_level && (
          <span className="error-text">{errors.faith_level}</span>
        )}
      </div>

      <div className="form-group">
        <label>Home church (optional)</label>
        <input
          type="text"
          value={data.home_church}
          onChange={(e) => updateData({ home_church: e.target.value })}
          placeholder="Grace Chapel"
          maxLength={200}
        />
      </div>

      <div className="setup-actions">
        <button className="secondary-button" onClick={onBack}>
          Back
        </button>
        <button className="primary-button" onClick={handleNext}>
          Continue
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// STEP 3: About
// =====================================================================
function Step3About({ onNext, onBack }) {
  const { data, updateData } = useProfileSetup();

  const toggleInterest = (interest) => {
    const current = data.interests;
    if (current.includes(interest)) {
      updateData({ interests: current.filter((i) => i !== interest) });
    } else if (current.length < 8) {
      updateData({ interests: [...current, interest] });
    }
  };

  return (
    <div className="setup-form">
      <div className="form-group">
        <label>Bio</label>
        <textarea
          value={data.bio}
          onChange={(e) => updateData({ bio: e.target.value })}
          placeholder="A few words about yourself, what you're looking for, what matters to you..."
          maxLength={500}
          rows={5}
        />
        <span className="char-count">{data.bio.length}/500</span>
      </div>

      <div className="form-group">
        <label>Interests (pick up to 8)</label>
        <div className="interests-grid">
          {INTERESTS.map((interest) => {
            const selected = data.interests.includes(interest);
            return (
              <button
                key={interest}
                type="button"
                className={`interest-chip ${selected ? 'selected' : ''}`}
                onClick={() => toggleInterest(interest)}
              >
                {interest}
              </button>
            );
          })}
        </div>
        <span className="char-count">{data.interests.length}/8 selected</span>
      </div>

      <div className="setup-actions">
        <button className="secondary-button" onClick={onBack}>
          Back
        </button>
        <button className="primary-button" onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// STEP 4: Photos
// =====================================================================
function Step4Photos({ onBack }) {
  const { data, updateData } = useProfileSetup();
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFilePick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (data.photos.length >= 6) {
      setError('Maximum 6 photos allowed');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await API.post('/api/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      updateData({ photos: [...data.photos, response.data] });
    } catch (err) {
    if (err.response?.data?.error === 'photo_rejected') {
        setError(err.response.data.message);
    } else {
        setError('Failed to upload photo. Please try again.');
    } 
  } finally {
      setUploading(false);
      // Reset input so picking the same file again still triggers onChange
      e.target.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await API.delete(`/api/photos/${photoId}`);
      updateData({ photos: data.photos.filter((p) => p.id !== photoId) });
    } catch (err) {
      setError('Failed to delete photo');
    }
  };

  const handleMove = (index, direction) => {
    const newPhotos = [...data.photos];
    const target = index + direction;
    if (target < 0 || target >= newPhotos.length) return;
    [newPhotos[index], newPhotos[target]] = [newPhotos[target], newPhotos[index]];
    updateData({ photos: newPhotos });
  };
const { setHasProfile } = useAuth();
  const handleFinish = async () => {
    if (data.photos.length === 0) {
      setError('Please add at least one photo');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Sync photo order if rearranged
      if (data.photos.length > 1) {
        await API.put('/api/photos/reorder', {
          photo_ids: data.photos.map((p) => p.id),
        });
      }

      // Create the profile
      await API.post('/api/profile', {
        full_name: data.full_name.trim(),
        dob: data.dob,
        gender: data.gender,
        denomination: data.denomination,
        faith_level: data.faith_level,
        home_church: data.home_church.trim(),
        bio: data.bio.trim(),
        interests: data.interests,
      });

      setHasProfile(true);  // ← add this line here
      navigate('/swipe');
    
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="setup-form">
      <p className="setup-hint">
        Add up to 6 photos. The first one is your main photo. Tap arrows to reorder.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="photos-grid">
        {[0, 1, 2, 3, 4, 5].map((slot) => {
          const photo = data.photos[slot];
          if (photo) {
            return (
              <div key={photo.id} className="photo-slot filled">
                <img src={photo.url} alt="" />
                {slot === 0 && <div className="primary-badge">Main</div>}
                <button
                  className="photo-delete"
                  onClick={() => handleDelete(photo.id)}
                  type="button"
                >
                  ✕
                </button>
                <div className="photo-reorder">
                  {slot > 0 && (
                    <button
                      type="button"
                      onClick={() => handleMove(slot, -1)}
                      title="Move up"
                    >
                      ↑
                    </button>
                  )}
                  {slot < data.photos.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleMove(slot, 1)}
                      title="Move down"
                    >
                      ↓
                    </button>
                  )}
                </div>
              </div>
            );
          }
          // Empty slot
          const isFirstEmpty = slot === data.photos.length;
          return (
            <div
              key={slot}
              className={`photo-slot empty ${isFirstEmpty ? 'clickable' : ''}`}
              onClick={() => isFirstEmpty && fileInputRef.current?.click()}
            >
              {isFirstEmpty && !uploading && <span className="plus">+</span>}
              {isFirstEmpty && uploading && <span className="uploading">Uploading...</span>}
            </div>
          );
        })}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFilePick}
        style={{ display: 'none' }}
      />

      <div className="setup-actions">
        <button
          className="secondary-button"
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </button>
        <button
          className="primary-button"
          onClick={handleFinish}
          disabled={submitting || data.photos.length === 0}
        >
          {submitting ? 'Finishing...' : 'Finish'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Helpers
// =====================================================================
function calculateAge(dobString) {
  const birth = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}